import { ACTIONS } from '~/types';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TagDialogProps {
  isOpen: boolean;
  isEditModal: boolean;
  tagName: string;
  tagDescription: string;
  notificationSound: boolean;
  tagAction: ACTIONS;
  tagColor: string;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setTagName: (value: string) => void;
  setTagDescription: (value: string) => void;
  setNotificationSound: (value: boolean) => void;
  setTagAction: (value: ACTIONS) => void;
  setTagColor: (value: string) => void;
  closeDialog: () => void;
}

export default function TagDialog({
  isOpen,
  isEditModal,
  tagName,
  tagDescription,
  notificationSound,
  tagAction,
  tagColor,
  handleSubmit,
  setTagName,
  setTagDescription,
  setNotificationSound,
  setTagAction,
  setTagColor,
  closeDialog,
}: TagDialogProps) {
  const [t] = useTranslation('teams');
  const dialogRef = useRef<HTMLDialogElement>(null);

  const actions = [
    { value: ACTIONS.KEEP_CONVERSATION, label: t('no_action') },
    { value: ACTIONS.REDIRECT_AND_NOTIFY, label: t('redirect_and_notify') },
  ];

  const colors = [
    // Brand Colors
    '#2c3080', // Brand Primary Navy
    '#1fedcc', // Brand Secondary Turquoise
    '#dac2fe', // Brand Lavender
    '#ff8270', // Brand Coral
    '#f8f2a4', // Brand Yellow
    '#E0E0E0', // Default Gray
    // Additional Pastel Colors
    '#FFB3B3', // Rosa pastel
    '#FFD1DC', // Rosa claro
    '#FFCBA4', // Melocotón pastel
    '#FFE5CC', // Naranja pastel
    '#FFF4BD', // Amarillo pastel
    '#E2F0CB', // Verde lima pastel
    '#B5EAD7', // Menta pastel
    '#C7CEEA', // Azul lavanda
    '#A2D2FF', // Azul cielo pastel
    '#BDE0FE', // Azul bebé
    '#E2D1F9', // Lavanda pastel
    '#fef3cd', // Amber 100
    '#ccfef6', // Teal 100
    '#feeae6', // Orange 100
  ];

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.showModal();
    } else if (dialogRef.current) {
      dialogRef.current.close();
    }
  }, [isOpen]);

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      onClick={(e) => {
        if (e.target === dialogRef.current) {
          closeDialog();
        }
      }}
    >
      <div
        className="max-w-md modal-box flex w-full flex-col gap-4 rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {isEditModal ? t('edit_tag') : t('add_tag')}
          </h2>
          <button
            onClick={closeDialog}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col">
            <label
              htmlFor="team-name"
              className="text-sm font-medium text-gray-700"
            >
              {t('name')}
            </label>
            <input
              type="text"
              id="team-name"
              name="team-name"
              className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder={t('placeholder_name')}
              required
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="team-description"
              className="text-sm font-medium text-gray-700"
            >
              {t('description')}
            </label>
            <textarea
              id="team-description"
              name="team-description"
              className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder={t('placeholder_description')}
              rows={3}
              value={tagDescription}
              onChange={(e) => setTagDescription(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="tag-action"
              className="text-sm font-medium text-gray-700"
            >
              {t('action')}
            </label>
            <select
              id="tag-action"
              name="tag-action"
              className="rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
              value={tagAction}
              onChange={(e) => setTagAction(e.target.value as ACTIONS)}
            >
              {actions.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              {t('color')}
            </label>
            <div className="grid grid-cols-6 gap-2">
              {colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`h-8 w-8 rounded-full border-2 transition-all ${tagColor === color ? 'scale-110 border-gray-600' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setTagColor(color)}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="notification-sound"
              name="notification-sound"
              className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
              checked={notificationSound}
              onChange={(e) => setNotificationSound(e.target.checked)}
            />
            <label
              htmlFor="notification-sound"
              className="text-sm font-medium text-gray-700"
            >
              {t('notification_sound')}
            </label>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeDialog}
              className="rounded-lg border px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="rounded-lg bg-pink-500 px-4 py-2 text-white hover:bg-pink-600"
            >
              {isEditModal ? t('edit_tag') : t('create_tag')}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
